import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { pushNotificationService } from '../services/Notification';
import { UserContext } from '../context';

const PushNotificationContext = createContext();

export const usePushNotification = () => {
  const context = useContext(PushNotificationContext);
  if (!context) {
    throw new Error('usePushNotification must be used within PushNotificationProvider');
  }
  return context;
};

export const PushNotificationProvider = ({ children }) => {
  const { user } = useContext(UserContext);
  const [permission, setPermission] = useState('default');
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasRequestedPermission = useRef(false);

  // Initialize permission state safely (Notification might not exist on all platforms)
  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);

  // Initialize push notifications
  const initializePushNotifications = useCallback(async () => {
    // Use isPushSupported() which has Safari-specific logic
    if (!pushNotificationService.isPushSupported()) {
      console.log('[PushNotificationProvider] Push notifications not supported on this platform', {
        platform: pushNotificationService.platform,
        isMacSafari: pushNotificationService.isMacSafari,
        isIOS: pushNotificationService.isIOS,
        isStandalone: pushNotificationService.isStandalone
      });
      return;
    }

    console.log('[PushNotificationProvider] Initializing push notifications...', {
      platform: pushNotificationService.platform,
      permission: 'Notification' in window ? Notification.permission : 'N/A'
    });

    try {
      setLoading(true);
      setError(null);

      // Check current subscription
      const status = await pushNotificationService.checkSubscriptionStatus();
      setSubscription(status.subscription);

      // Safari requires user gesture for permission request
      // Don't auto-request permission - let the user click a button
      // Just sync permission state
      if ('Notification' in window) {
        setPermission(Notification.permission);
      }

      // Auto-subscribe if permission already granted and not subscribed
      if (Notification.permission === 'granted' && !status.subscribed) {
        await subscribeToNotifications();
      }
    } catch (err) {
      console.error('Error initializing push notifications:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Subscribe to push notifications
  const subscribeToNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const sub = await pushNotificationService.subscribeToPushNotifications();
      setSubscription(sub);
      return sub;
    } catch (err) {
      console.error('Error subscribing to push notifications:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Unsubscribe from push notifications
  const unsubscribeFromNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      
      await pushNotificationService.unsubscribeFromPushNotifications();
      setSubscription(null);
    } catch (err) {
      console.error('Error unsubscribing from push notifications:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Send test notification
  const sendTestNotification = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await pushNotificationService.sendTestNotification();
      return result;
    } catch (err) {
      console.error('Error sending test notification:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update permission state when it changes
  useEffect(() => {
    const handlePermissionChange = () => {
      setPermission(Notification.permission);
    };

    if ('permissions' in navigator) {
      navigator.permissions.query({ name: 'notifications' })
        .then((permissionStatus) => {
          permissionStatus.onchange = handlePermissionChange;
        });
    }
  }, []);

  // Initialize on mount
  useEffect(() => {
    initializePushNotifications();
  }, [initializePushNotifications]);

  // Auto-request notification permission when user is logged in
  // Works on Android, iOS, Mac Safari, and desktop browsers
  useEffect(() => {
    // Check if Notification API is available (works for Safari Mac, Chrome, Firefox, etc.)
    const hasNotificationAPI = 'Notification' in window;
    const currentPermission = hasNotificationAPI ? Notification.permission : 'denied';
    const isSafari = pushNotificationService.isMacSafari || pushNotificationService.isIOS;
    
    console.log('[PushNotificationProvider] Checking notification permission request conditions:', {
      user: !!user,
      hasNotificationAPI,
      currentPermission,
      hasRequestedPermission: hasRequestedPermission.current,
      platform: pushNotificationService.platform,
      isMacSafari: pushNotificationService.isMacSafari,
      isIOS: pushNotificationService.isIOS,
      isSafari,
      safariVersion: pushNotificationService.getSafariVersion ? pushNotificationService.getSafariVersion() : 'N/A'
    });

    // Store cleanup function for Safari fallback event listeners
    let cleanupFn = null;

    // Only request if:
    // 1. User is logged in
    // 2. Notification API is available
    // 3. Permission is still 'default' (not asked yet)
    // 4. Haven't already requested in this session
    if (
      user &&
      hasNotificationAPI &&
      currentPermission === 'default' &&
      !hasRequestedPermission.current
    ) {
      hasRequestedPermission.current = true;
      
      console.log('[PushNotificationProvider] Requesting notification permission for logged-in user', {
        platform: pushNotificationService.platform,
        isSafari
      });

      // Request permission - Safari 16+ and all other modern browsers support this
      const requestPermission = async () => {
        try {
          let result;
          
          // Handle Safari's callback-based API fallback
          if (typeof Notification.requestPermission === 'function') {
            const permissionPromise = Notification.requestPermission();
            if (permissionPromise && typeof permissionPromise.then === 'function') {
              result = await permissionPromise;
            } else {
              // Fallback for callback-based API (older Safari)
              result = await new Promise(resolve => {
                Notification.requestPermission(resolve);
              });
            }
          }
          
          console.log('[PushNotificationProvider] Permission result:', result);
          setPermission(result);

          if (result === 'granted' && pushNotificationService.isPushSupported()) {
            try {
              await subscribeToNotifications();
            } catch (subError) {
              console.error('[PushNotificationProvider] Error subscribing after permission granted:', subError);
            }
          }
        } catch (err) {
          console.error('[PushNotificationProvider] Error requesting permission:', err);
          
          // If direct request failed on Safari, try with user gesture fallback
          if (isSafari) {
            console.log('[PushNotificationProvider] Safari: Direct request failed, setting up user gesture fallback');
            hasRequestedPermission.current = false; // Reset to allow gesture-based request
            
            const handleUserGesture = async () => {
              if (hasRequestedPermission.current) return;
              hasRequestedPermission.current = true;
              
              try {
                const gestureResult = await Notification.requestPermission();
                console.log('[PushNotificationProvider] Safari gesture-based permission result:', gestureResult);
                setPermission(gestureResult);
                
                if (gestureResult === 'granted' && pushNotificationService.isPushSupported()) {
                  await subscribeToNotifications();
                }
              } catch (gestureErr) {
                console.error('[PushNotificationProvider] Safari gesture-based permission error:', gestureErr);
              }
              
              document.removeEventListener('click', handleUserGesture, true);
              document.removeEventListener('touchend', handleUserGesture, true);
            };
            
            document.addEventListener('click', handleUserGesture, { capture: true, once: true });
            document.addEventListener('touchend', handleUserGesture, { capture: true, once: true });
            
            cleanupFn = () => {
              document.removeEventListener('click', handleUserGesture, true);
              document.removeEventListener('touchend', handleUserGesture, true);
            };
          }
        }
      };

      // Add a small delay to ensure everything is initialized (helpful for Safari)
      setTimeout(requestPermission, 500);
    }

    return () => {
      if (cleanupFn) {
        cleanupFn();
      }
    };
  }, [user]);

  const value = {
    permission,
    subscription,
    loading,
    error,
    isSupported: pushNotificationService.isPushSupported(),
    platform: pushNotificationService.platform,
    isMacSafari: pushNotificationService.isMacSafari,
    isIOS: pushNotificationService.isIOS,
    subscribeToNotifications,
    unsubscribeFromNotifications,
    sendTestNotification,
    initializePushNotifications,
    checkStatus: pushNotificationService.checkSubscriptionStatus.bind(pushNotificationService)
  };

  return (
    <PushNotificationContext.Provider value={value}>
      {children}
    </PushNotificationContext.Provider>
  );
};